import { DEVICE, DEVICE_TYPE, TRACKING_EVENTS } from "../../constants";
import { PAYMENT_SCREENS } from "constants/paymentScreens";
import { PAYMENT_ERROR_MESSAGE } from "constants/paymentStatus";
import { isAndroid, isIos } from "helpers/deviceType";
import { trackAppCallbackFailed, trackAppCallbackSuccess, trackWebviewTabActive, trackWebviewTabInActive } from "layouts/Payment/common/dependencies/tracking";
import { logger } from "logging";
import { INFO_EVENTS } from "logging/newRelicLogger";
import { allTrackingHandlerForApps } from "shared/helpers/utils/trackingHandler";
import { getRemoteConfig } from "shared/helpers";
import { checkIsExperimentEligible } from "wrapper/ExperimentAndSwitchWrapper/helpers";
import { isServer } from "./isServer";


interface CallbackConfigType {
  name: string;
  data: any;
}

interface CallbackErrorConfig {
  error: string;
  callbackName: string;
}

export interface ICustomerConfig {
  customerId: string;
  loginEmail: string;
  loginUsername?: string;
  firstname: string;
  lastname: string;
  mobile: string;
  is_loyal: any;
  form_key: string;
}

export class UserData {
  private static config: {};

  public static setUserDataConfig(config: {}) {
    UserData.config = config;
  }

  public static getUserDataConfig() {
    return (UserData.config || {}) as ICustomerConfig;
  }
}

/**
 * App callbacks which we are calling.
 */
export enum AppCallback {
  GET_UPI_APPS = "getUPIApps", // for getting the PSP installed apps.
  DO_OPEN_INTENT_FLOW = "doOpenIntentFlow", // for open the intent flow for payu and cashfree pg.
  DO_OPEN_NATIVE_ORDER_CONFIRMATION = "doOpenNativeOrderConfirmation", // for open native(android & ios) order confirmation page.
  DO_REDIRECT_TO_CART = "doRedirectToCart", // for redirecting to the native(android & ios) cart page.
  CART_NOT_FOUND = "cartNotFound",
  DO_REDIRECT_TO_HOME = "doRedirectToHome", // for redirecting to the native(android & ios) home page.
  ON_PAGE_LOAD = "onPageLoad", // for passing the remote config info.
  DO_OPEN_NATIVE_CHECKOUT_FLOW = "doOpenNativeCheckoutFlow", // for open native(android & ios) checkout page.
  GET_AUTO_DETECT_ADDRESS = "getAutoDetectAddress", // for getting the auto detect address information.
  DO_OPEN_UPI_INTENT_VIA_RAZORPAYSDK = "doOpenUpiIntentViaRazorpaySdk", // for open the intent flow for razorpay pg.
  DO_TRACK = "doTrack", // for tracking.
  DO_SET_REDIRECTION_URL = "doSetRedirectionUrl", // for send the redirection url to apps and they can use the url.
  DO_START_LISTEN_OTP_AUTO_DETECT = "doStartListenOtpAutoDetect", // for listen the paytm auto detect otp.
  DO_STOP_LISTEN_OTP_AUTO_DETECT = "doStopListenOtpAutoDetect", // for stop listen the otp broadcasting.
  DO_UPDATE_CART_INFO = "doUpdateCartInfo", // for update the cart refresh data in apps memory.
  DO_OPEN_NEW_WEBVIEW_INSTANCE = "openNewWebviewInstance", // for opening new webview instance.
  DO_CLOSE_BOTTOM_SHEET = "closeBottomSheet", //for closing express checkout bottom sheet.
  ADJUST_BOTTOM_SHEET_HEIGHT = "adjustBottomSheetHeight" // for adjusting the bottom sheet height
}

/**
 * Web callbacks which is attached on window and called by apps.
 */
export enum WebCallback {
  SET_UPI_APPS = "setUPIAppsAndTiles", // apps call it after the getUPIApps callback and get the PSP installed apps.
  GET_CUSTOMER_INFO = "getCustomerInfo", // apps call it after onPageLoad callback and get customer information such as customerId.
  DO_OPEN_INTENT_ERROR_CALLBACK = "openIntentErrorHandler", // apps call it when we get the error while opening the intent flow from all pgs.
  APP_GENERIC_CALLBACK_ERROR_HANDLER = "appCallbackErrorHandler", // it is a generic callback handler and when any callback get failed then they will call it and send the error.
  UPI_INTENT_TRANSACTION_COMPLETE = "onUpiIntentTransactionComplete", // android call it after intent transaction complete and incase of ios web call it after polling.
  SET_AUTO_DETECT_ADDRESS = "setAutoDetectAddress", // apps call it after getAutoDetectAddress and get the auto detect address information.
  AUTO_DETECT_ERROR_HANDLER = "autoDetectErrorHandler", // it is for auto detect error.
  AUTO_DETECT_TRACKING = "setAutoDetectPermissionTracking", // it is for auto detect permission tracking.
  DO_OPEN_CRED_INTENT_ERROR_CALLBACK = "openCredIntentErrorHandler", // apps call it when we get the error while opening the intent flow from cred.
  DO_GET_OTP_AUTO_DETECT = "doGetOtpAutoDetect", // for get the paytm auto detect otp.
  DO_HANDLE_NATIVE_BACK = "doHandleNativeBack",
}

/**
 * @param callbackConfig: callbackConfigType
 * @description It is for calling the apps  incase for webview.
 * callback attached on window, called by web && provided by app.
 */
export const doCallAppCallback = (callbackConfig: CallbackConfigType) => {
  const callbackName = callbackConfig?.name;
  const callbackData = callbackConfig?.data || {};
  logger.info(INFO_EVENTS.WEBVIEW_CALLBACK, { data: `doCallAppCallback: ${callbackName}` });
  try {
    if (isAndroid()) {
      (window as any).AndroidFunction[callbackName](JSON.stringify(callbackData));
      trackAppCallbackSuccess({
        callbackName,
      });
    } else if (isIos()) {
      (window as any).webkit.messageHandlers[callbackName].postMessage(callbackData);
      trackAppCallbackSuccess({
        callbackName,
      });
    }
  } catch (error) {
    trackAppCallbackFailed({
      error,
      callbackName,
    });
    logger.error(error as Error, `Error in doCallAppCallback for callback: ${callbackName}`);
  }
};

/**
 * @description It is for getting the device type for webview.
 * if android then we are setting up ANDROID.
 * if ios then we are setting up IOS.
 */
export const getMobileAppDeviceType = () => {
  let deviceType = DEVICE_TYPE.MOBILE;
  if (isAndroid()) {
    deviceType = DEVICE_TYPE.ANDROID;
  } else if (isIos()) {
    deviceType = DEVICE_TYPE.IOS;
  }
  return deviceType;
};

/**
 * @param e error config.
 * @param openErrorModal for opening a error modal.
 * @param paymentScreen for getting the payment screen
 * @description It is generic callback error handler for webview.
 * if any app callback fails then they they will call this callback.
 */
export const webviewCallbacksGenericErrorHandler = (
  e: CallbackErrorConfig,
  openErrorModal: any,
  paymentScreen: string,
  setLoading: any
) => {
  console.log("appCallbackErrorHandler callback called", e);
  const { error, callbackName } = e;

  setLoading(false);
  trackAppCallbackFailed({
    error,
    callbackName,
  });
  const err = new Error(`callbackName:${callbackName}-${error}`);
  logger.error(err);
  switch (callbackName) {
    case AppCallback.DO_REDIRECT_TO_CART:
    case AppCallback.ON_PAGE_LOAD:
    case WebCallback.GET_CUSTOMER_INFO:
    case AppCallback.GET_UPI_APPS:
    case WebCallback.SET_UPI_APPS:
      logger.error(new Error(`${AppCallback.DO_OPEN_NATIVE_CHECKOUT_FLOW} called`), {
          callbackName: callbackName,
          message: error,
      });
      setTimeout(() => {
        doCallAppCallback({
          name: AppCallback.DO_OPEN_NATIVE_CHECKOUT_FLOW,
          data: {
            callbackName: callbackName,
            message: error,
          },
        });
      }, 100);
      break;
    case AppCallback.DO_OPEN_INTENT_FLOW:
    case AppCallback.DO_OPEN_UPI_INTENT_VIA_RAZORPAYSDK:
      if (paymentScreen === PAYMENT_SCREENS.ALL) {
        (window as any)?.[WebCallback.DO_OPEN_INTENT_ERROR_CALLBACK]?.();
      } else {
        openErrorModal(PAYMENT_ERROR_MESSAGE.SOMETHING_WENT_WRONG, error);
      }
      break;
    default:
      openErrorModal(PAYMENT_ERROR_MESSAGE.SOMETHING_WENT_WRONG, error);
  }
};

/**
 * @param config: {callbackName: string, callbackDeclaration: any}
 * @description It is for attaching the web callback on window for webview
 * so apps can use attached callback.
 * callback passed to apps, called by app && provided by web
 */
export const doAttachWebCallback = (config: { callbackName: string; callbackDeclaration: any }) => {
  const { callbackName, callbackDeclaration } = config;
  logger.info(INFO_EVENTS.WEBVIEW_CALLBACK, { data: `doAttachWebCallback: ${callbackName}` });
  (window as any)[callbackName] = callbackDeclaration;
};

/**
 * apps will call it after onPageLoad and set the customerData in it.
 */
export const getCustomerInfo = (customerConfig: ICustomerConfig) => {
  logger.info(INFO_EVENTS.WEBVIEW_CALLBACK, { data:`getCustomerInfo callback called, ${customerConfig?.customerId}` });
  console.log("__customerConfig", customerConfig);
  const data = {
    result: {
      form_key: customerConfig?.form_key,
      customerData: {
        id: customerConfig?.customerId,
        loginEmail: customerConfig?.loginEmail,
        gfirstname: customerConfig?.firstname,
        glastname: customerConfig?.lastname,
        mobile: customerConfig?.mobile,
        is_loyal: customerConfig?.is_loyal,
      },
    },
  };

  if (customerConfig?.customerId) {
    UserData.setUserDataConfig(data);
  }
};

export const isAppCallbackPresent = ({ callbackName }: {callbackName: string}) => {
  let callback;
  if (isAndroid()) {
    callback = (window as any)?.AndroidFunction?.[callbackName];
  } else if (isIos()) {
    callback = (window as any)?.webkit?.messageHandlers?.[callbackName];
  }

  if (!callback) {
    return false;
  }
  return true;
};

  /**
   * handleReactBlankScreen function is to fix blank screen issue on andoird webview
   * This issue was observed when user is on payment page and then open multiple other apps
   * Device Specific Issue: OnePlus Nord, One Plus CE
   * Fix: The black screen gets removed on touch and the webpage is as it is behind that
   * Solution: We have triggred a scroll event on visiblity change event
   */
   export const handleReactBlankScreen = () => {
    try {
      if (!isServer()) {
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState == "visible") {
            window.scrollBy({
              top: 1,
              left: 0,
              behavior: "smooth",
            });
            allTrackingHandlerForApps({
              event: TRACKING_EVENTS.WEBVIEW_TAB_ACTIVE,
              isApp: true,
            });
            trackWebviewTabActive();
          } else {
            allTrackingHandlerForApps({
              event: TRACKING_EVENTS.WEBVIEW_TAB_INACTIVE,
              isApp: true,
            });
            trackWebviewTabInActive();
          }
        });
      }
    } catch (error) {
      logger.error("Error in handleReactBlankScreen", error);
    }
  };

  /**
   * This feature is to check if refresh api call is required from web in case of webview
   * It is being set in local storage by apps. This remote config flag is added to disable it in case of any issue
   * This switch can be removed later on.
   */
  export const checkIsGetRefreshFromCacheEnabled = () => {
    let isGetRefreshFromCacheExperimentEnabled: any = false;
    try {
      const remoteConfig = getRemoteConfig();
      const configs = remoteConfig.configs;
      /* eslint-disable no-undef */
      const deviceTypeFromAppData = __APP_DATA__.deviceType;
      const deviceType =
        deviceTypeFromAppData === DEVICE.DESKTOP ? deviceTypeFromAppData : getMobileAppDeviceType();
        //no need to handle userdata for this, will have to do prop drilling to get the value
        isGetRefreshFromCacheExperimentEnabled = checkIsExperimentEligible(
        "fetchRefreshDataFromCache",
        configs,
        deviceType
      );
      return isGetRefreshFromCacheExperimentEnabled;
    } catch (error) {
      logger.error(error, "Error in checkIsGetRefreshFromCacheExperimentEnabled");
    }
    return isGetRefreshFromCacheExperimentEnabled;
  };
